import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Layers,
  Pencil,
  Plus,
  Trash2,
  X
} from "lucide-react";
import { ScopingSchemaEditor } from "./useScopingSchemaEditor";
import { ScopingFormPreview } from "./ScopingFormPreview";
import ConfirmationModal from "../common/ConfirmationModal";
import {
  FIELD_TYPE_OPTIONS,
  isChoiceType,
  questionTypeIcon,
  questionTypeLabel
} from "./scoping-question-ui";
import { salesTokens, SalesButton } from "./sales-ui";
import { ScopingQuestion } from "./scoping-schema";

type Props = {
  editor: ScopingSchemaEditor;
  templateName?: string;
  showPreview?: boolean;
  compact?: boolean;
  validationErrors?: string[];
};

export const ScopingFormBuilder: React.FC<Props> = ({
  editor,
  templateName,
  showPreview = true,
  compact = false,
  validationErrors = []
}) => {
  const [mode, setMode] = useState<"edit" | "split">("edit");
  const [sectionIdxToDelete, setSectionIdxToDelete] = useState<number | null>(null);
  const [questionToDelete, setQuestionToDelete] = useState<{ sectionIdx: number; qid: string } | null>(null);

  const {
    schema,
    activeSectionIdx,
    setActiveSectionIdx,
    previewSectionIdx,
    setPreviewSectionIdx,
    activeSection,
    previewSection,
    expandedQids,
    toggleQuestionExpand,
    collapseAllQuestions,
    addSection,
    removeSection,
    updateSectionDetails,
    addQuestion,
    removeQuestion,
    duplicateQuestion,
    updateQuestionLabel,
    updateQuestionKeyId,
    toggleQuestionRequired,
    updateQuestionType,
    addQuestionOption,
    updateQuestionOption,
    removeQuestionOption
  } = editor;

  const sections = schema?.sections ?? [];
  const railMaxH = compact ? "max-h-[320px]" : "max-h-[min(520px,55vh)]";

  const syncPreviewToActive = (idx: number) => {
    setActiveSectionIdx(idx);
    setPreviewSectionIdx(idx);
  };

  const renderQuestionCard = (q: ScopingQuestion, sectionIdx: number) => {
    const expanded = expandedQids[q.qid] ?? false;
    const choice = isChoiceType(q.type);

    if (!expanded) {
      return (
        <div
          key={q.qid}
          className="group flex items-center gap-2 rounded-lg border border-slate-200/90 bg-white px-3 py-2 hover:border-ctb-blue/25 hover:shadow-sm transition"
        >
          <button
            type="button"
            className="flex flex-1 min-w-0 items-center gap-2.5 text-left"
            onClick={() => toggleQuestionExpand(q.qid)}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 border border-slate-200/80">
              {questionTypeIcon(q.type)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5">
                <span className="text-[11px] font-semibold text-slate-800 truncate">
                  {q.label || "Untitled"}
                </span>
                {q.required && (
                  <span className="text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200/80 px-1 rounded">
                    Required
                  </span>
                )}
              </span>
              <span className="flex items-center gap-1.5 text-[10px] text-slate-400 mt-0.5">
                <code className="font-mono text-ctb-blue bg-ctb-blue/5 px-1 rounded">{q.qid}</code>
                <span>·</span>
                <span>{questionTypeLabel(q.type)}</span>
              </span>
            </span>
          </button>
          <div className="flex items-center gap-0.5 opacity-80 group-hover:opacity-100">
            <button
              type="button"
              title="Duplicate"
              onClick={() => duplicateQuestion(sectionIdx, q.qid)}
              className="p-1.5 text-slate-400 hover:text-ctb-blue hover:bg-slate-100 rounded-md transition"
            >
              <Copy size={12} />
            </button>
            <button
              type="button"
              title="Delete"
              onClick={() => setQuestionToDelete({ sectionIdx, qid: q.qid })}
              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition"
            >
              <Trash2 size={12} />
            </button>
            <button
              type="button"
              onClick={() => toggleQuestionExpand(q.qid)}
              className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-md"
            >
              <ChevronDown size={12} />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        key={q.qid}
        className="rounded-lg border border-ctb-blue/20 bg-white p-4 shadow-sm space-y-3"
      >
        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
          <button
            type="button"
            className="flex items-center gap-2 text-[11px] font-semibold text-ctb-blue"
            onClick={() => toggleQuestionExpand(q.qid)}
          >
            {questionTypeIcon(q.type)}
            Edit question
          </button>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => duplicateQuestion(sectionIdx, q.qid)}
              className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-md"
              title="Duplicate"
            >
              <Copy size={12} />
            </button>
            <button
              type="button"
              onClick={() => setQuestionToDelete({ sectionIdx, qid: q.qid })}
              className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-md"
            >
              <Trash2 size={12} />
            </button>
            <button
              type="button"
              onClick={() => toggleQuestionExpand(q.qid)}
              className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-md"
            >
              <ChevronUp size={12} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-8 space-y-1">
            <label className="text-[10px] font-medium text-slate-500">Label</label>
            <input
              type="text"
              value={q.label}
              onChange={(e) => updateQuestionLabel(sectionIdx, q.qid, e.target.value)}
              onBlur={() => editor.autoGenerateKeyId(sectionIdx, q.qid)}
              className={salesTokens.input}
              placeholder="Question text"
            />
          </div>
          <div className="md:col-span-2 space-y-1">
            <label className="text-[10px] font-medium text-slate-500">Type</label>
            <select
              value={q.type}
              onChange={(e) => updateQuestionType(sectionIdx, q.qid, e.target.value)}
              className={salesTokens.input}
            >
              {FIELD_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 flex flex-col justify-end">
            <label className="text-[10px] font-medium text-slate-500 mb-1">Required</label>
            <button
              type="button"
              onClick={() => toggleQuestionRequired(sectionIdx, q.qid)}
              className={`relative w-9 h-5 rounded-full transition ${
                q.required ? "bg-ctb-blue" : "bg-slate-200"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  q.required ? "translate-x-4" : ""
                }`}
              />
            </button>
          </div>
        </div>

        {choice && (
          <div className="pl-3 border-l-2 border-ctb-blue/20 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-medium text-slate-500">Answer options</span>
              <SalesButton
                variant="ghost"
                onClick={() => addQuestionOption(sectionIdx, q.qid)}
              >
                <Plus size={12} />
                Add option
              </SalesButton>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(q.options || []).map((opt, optIdx) => {
                const optLabel = typeof opt === "string" ? opt : opt.label || "";
                return (
                  <div
                    key={optIdx}
                    className="flex gap-2 p-2 rounded-lg border border-slate-200 bg-slate-50/50"
                  >
                    <div className="flex-1">
                      <input
                        type="text"
                        value={optLabel}
                        onChange={(e) =>
                          updateQuestionOption(sectionIdx, q.qid, optIdx, "label", e.target.value)
                        }
                        placeholder="Option label"
                        className={salesTokens.input}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeQuestionOption(sectionIdx, q.qid, optIdx)}
                      className="p-1 text-slate-400 hover:text-rose-600 self-center"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const editorPane = (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 min-h-0">
      {/* Sections rail */}
      <aside className={`lg:col-span-3 flex flex-col ${salesTokens.panel} p-3`}>
        <div className="flex items-center justify-between pb-2 border-b border-slate-100 shrink-0">
          <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            <Layers size={12} />
            Sections
          </span>
          <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
            {sections.length}
          </span>
        </div>
        <div className={`space-y-1 mt-2 overflow-y-auto pr-0.5 ${railMaxH} flex-1`}>
          {sections.map((sec, idx) => {
            const active = idx === activeSectionIdx;
            return (
              <button
                key={sec.id}
                type="button"
                onClick={() => syncPreviewToActive(idx)}
                className={`w-full text-left px-2.5 py-2 rounded-lg border text-[11px] transition ${
                  active
                    ? "bg-ctb-blue-0/50 border-ctb-blue/30 text-ctb-blue font-semibold shadow-sm"
                    : "border-transparent hover:bg-slate-50 text-slate-600"
                }`}
              >
                <span className="block truncate">{sec.title || "Untitled"}</span>
                <span className="text-[10px] text-slate-400 font-normal">
                  {sec.questions?.length ?? 0} questions
                </span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={addSection}
          className="mt-2 w-full py-2 border border-dashed border-slate-300 rounded-lg text-[10px] font-semibold text-slate-500 hover:border-ctb-blue/40 hover:text-ctb-blue hover:bg-ctb-blue-0/20 transition flex items-center justify-center gap-1"
        >
          <Plus size={12} />
          Add section
        </button>
      </aside>

      {/* Section editor */}
      <div className="lg:col-span-9 min-h-0 flex flex-col">
        {activeSection ? (
          <div className={`flex flex-col flex-1 min-h-0 ${salesTokens.panel}`}>
            <div className="px-4 py-3 border-b border-slate-100 space-y-2 shrink-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 space-y-2 min-w-0">
                  <label className="text-[10px] font-medium text-slate-500 flex items-center gap-1">
                    <Pencil size={10} />
                    Section title
                  </label>
                  <input
                    type="text"
                    value={activeSection.title}
                    onChange={(e) =>
                      updateSectionDetails(activeSectionIdx, "title", e.target.value)
                    }
                    className="w-full text-sm font-bold text-slate-900 border-b border-slate-200 focus:border-ctb-blue focus:outline-none py-1 bg-transparent"
                    placeholder="Section title"
                  />
                  <input
                    type="text"
                    value={activeSection.description || ""}
                    onChange={(e) =>
                      updateSectionDetails(activeSectionIdx, "description", e.target.value)
                    }
                    className={`${salesTokens.input} text-slate-500`}
                    placeholder="Help text shown to the client"
                  />
                </div>
                <SalesButton
                  variant="ghost"
                  onClick={() => setSectionIdxToDelete(activeSectionIdx)}
                  className="text-rose-600 hover:bg-rose-50 shrink-0"
                >
                  <Trash2 size={12} />
                  Delete
                </SalesButton>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-[200px]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Questions ({activeSection.questions.length})
                </span>
                {activeSection.questions.length > 0 && (
                  <button
                    type="button"
                    onClick={collapseAllQuestions}
                    className="text-[10px] text-ctb-blue font-medium hover:underline"
                  >
                    Collapse all
                  </button>
                )}
              </div>
              {activeSection.questions.map((q) =>
                renderQuestionCard(q, activeSectionIdx)
              )}
              <SalesButton
                variant="secondary"
                onClick={() => addQuestion(activeSectionIdx)}
                className="mt-2"
              >
                <Plus size={14} />
                Add question
              </SalesButton>
            </div>
          </div>
        ) : (
          <div className={`${salesTokens.panel} p-10 text-center`}>
            <p className="text-[11px] text-slate-500">Add a section to start building.</p>
            <SalesButton className="mt-3" onClick={addSection}>
              <Plus size={14} />
              Create section
            </SalesButton>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {validationErrors.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
          <p className="font-semibold mb-1">Fix before saving:</p>
          <ul className="list-disc list-inside space-y-0.5 opacity-90">
            {validationErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {showPreview && (
        <div className="flex items-center gap-1 p-0.5 bg-slate-100 rounded-lg w-fit border border-slate-200/80">
          {(
            [
              { id: "edit" as const, label: "Editor", icon: Pencil },
              { id: "split" as const, label: "Split", icon: Layers }
            ] as const
          )
            .filter((t) => !(compact && t.id === "split"))
            .map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-semibold transition ${
                  mode === id
                    ? "bg-white text-ctb-blue shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon size={12} />
                {label}
              </button>
            ))}
        </div>
      )}

      {mode === "edit" && editorPane}

      {mode === "split" && showPreview && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 min-h-0">
          <div className="min-w-0">{editorPane}</div>
          <div className={`${salesTokens.panel} min-h-[360px] xl:min-h-0 overflow-hidden sticky top-2`}>
            <ScopingFormPreview
              schema={schema}
              section={previewSection}
              sectionIndex={previewSectionIdx}
              totalSections={sections.length}
              onSectionChange={setPreviewSectionIdx}
              templateName={templateName}
            />
          </div>
        </div>
      )}
      {sectionIdxToDelete !== null && (
        <ConfirmationModal
          isOpen={sectionIdxToDelete !== null}
          onClose={() => setSectionIdxToDelete(null)}
          onConfirm={() => {
            removeSection(sectionIdxToDelete);
            setSectionIdxToDelete(null);
          }}
          title="Delete Section"
          description="Are you sure you want to delete this section and all its questions?"
          warning="This action cannot be undone. All questions and logic inside this section will be permanently deleted."
          confirmText="Delete Section"
          cancelText="Cancel"
        />
      )}

      {questionToDelete !== null && (
        <ConfirmationModal
          isOpen={questionToDelete !== null}
          onClose={() => setQuestionToDelete(null)}
          onConfirm={() => {
            removeQuestion(questionToDelete.sectionIdx, questionToDelete.qid);
            setQuestionToDelete(null);
          }}
          title="Delete Question"
          description="Are you sure you want to delete this question?"
          warning="This will permanently remove the question field from the scoping intake form."
          confirmText="Delete Question"
          cancelText="Cancel"
        />
      )}
    </div>
  );
};
